import { cx } from 'classix'
import { Link } from 'gatsby'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import React from 'react'
import { useTranslation } from 'react-i18next'

import afiUBlackLogo from '@src/assets/svg/afiU-logo-black.svg'
import linkedInLogo from '@src/assets/svg/socials/linkedin.svg'
import i18n from '@src/config/i18n'
import locales from '@src/config/locales'

import Container from '../common/layout/Container'
import FooterColumn from './FooterColumn'
import FooterMobileColumn from './FooterMobileColumn'
import LocaleSelector from './LocaleSelector'

export type FooterColumnData = {
  title: string
  rootUrl?: string
  items: { title: string; href: string }[]
}

export default function Footer() {
  const { t } = useTranslation('revamp')
  const breakpoints = useBreakpoint()
  const locale = i18n.language

  const websiteColumns: FooterColumnData[] = [
    {
      title: t('footerColumns.keyCompetencies.title'),
      items: [
        {
          title: t('footerColumns.keyCompetencies.items.human'),
          href: locales[locale].humanCatalogPath,
        },
        {
          title: t('footerColumns.keyCompetencies.items.digitalTools'),
          href: locales[locale].digitalToolsCatalogPath,
        },
        {
          title: t('footerColumns.keyCompetencies.items.technologies'),
          href: locales[locale].technologiesCatalogPath,
        },
        {
          title: t('footerColumns.keyCompetencies.items.dataScience'),
          href: locales[locale].dataScienceCatalogPath,
        },
        {
          title: t('footerColumns.keyCompetencies.items.organizationalStrategy'),
          href: locales[locale].organizationalStrategyCatalogPath,
        },
        {
          title: t('footerColumns.keyCompetencies.items.projectManagement'),
          href: locales[locale].projectManagementCatalogPath,
        },
      ],
    },
    {
      title: t('footerColumns.organizationalPriorities.title'),
      items: [
        {
          title: t('footerColumns.organizationalPriorities.items.learningAndCoaching'),
          href: locales[locale].themeCoachingPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.customerExperience'),
          href: locales[locale].themeBusinessOperationsPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.artificialIntelligence'),
          href: locales[locale].themeDataIntelligencePath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.strategyAndPerformance'),
          href: locales[locale].themeStrategyPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.digitalTools'),
          href: locales[locale].themeAdoptionDigitalPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.employeeExperience'),
          href: locales[locale].themeFutureOfWorkPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.leadership'),
          href: locales[locale].themeLeadershipPath,
        },
        {
          title: t('footerColumns.organizationalPriorities.items.digitalTransformation'),
          href: locales[locale].themeDigitalTransformationPath,
        },
      ],
    },
    {
      title: t('footerColumns.certifiedTrainings.title'),
      items: [
        {
          title: t('footerColumns.certifiedTrainings.items.microsoft'),
          href: locales[locale].microsoftTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.aws'),
          href: locales[locale].awsTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.vmware'),
          href: locales[locale].vmwareTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.cisco'),
          href: locales[locale].ciscoTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.comptia'),
          href: locales[locale].comptiaTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.itil'),
          href: locales[locale].itilTrainingPath,
        },
      ],
    },
    {
      title: '‎', // Empty title for continuation of certifications
      items: [
        {
          title: t('footerColumns.certifiedTrainings.items.icf'),
          href: locales[locale].icfTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.pmi'),
          href: locales[locale].pmiTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.leanSixSigma'),
          href: locales[locale].leanTrainingPath,
        },
        {
          title: t('footerColumns.certifiedTrainings.items.scaledAgile'),
          href: locales[locale].scaleTrainingPath,
        },
      ],
    },
    {
      title: t('footerColumns.forOrganizations.title'),
      items: [
        {
          title: t('footerColumns.forOrganizations.items.privateGroups'),
          href: locales[locale].organizationGroupsPath,
        },
        {
          title: t('footerColumns.forOrganizations.items.coLearningWorkshops'),
          href: locales[locale].organizationWorkshopsPath,
        },
        {
          title: t('footerColumns.forOrganizations.items.coachingServices'),
          href: locales[locale].organizationCoachingPath,
        },
        {
          title: t('footerColumns.forOrganizations.items.customPrograms'),
          href: locales[locale].organizationProgramsPath,
        },
        {
          title: t('footerColumns.forOrganizations.items.privateConferences'),
          href: 'https://lp.afiexpertise.com/conferences-afi-u',
        },
      ],
    },
    {
      title: t('footerColumns.about.title'),
      items: [
        { title: t('footerColumns.about.items.whoWeAre'), href: locales[locale].aboutUsPath },
        { title: t('footerColumns.about.items.liveAfiU'), href: locales[locale].vivreAfiUPath },
        { title: t('footerColumns.about.items.team'), href: locales[locale].teamPath },
        { title: t('footerColumns.about.items.offices'), href: locales[locale].ourOfficesPath },
        {
          title: t('footerColumns.about.items.partnersAndCertifications'),
          href: locales[locale].partnersCertificationsPath,
        },
        { title: t('footerColumns.about.items.careers'), href: locales[locale].careersPath },
      ],
    },
    {
      title: t('footerColumns.resources.title'),
      items: [
        { title: t('footerColumns.resources.items.blog'), href: locales[locale].blogPath },
        {
          title: t('footerColumns.resources.items.fundingProgram'),
          href: locales[locale].fundingPath,
        },
        {
          title: t('footerColumns.resources.items.newsletter'),
          href: locales[locale].newsletterPath,
        },
        { title: t('footerColumns.resources.items.faq'), href: locales[locale].faqPath },
        { title: t('footerColumns.resources.items.contact'), href: locales[locale].contactPath },
      ],
    },
    {
      title: t('footerColumns.programs.title'),
      items: [
        {
          title: t('footerColumns.programs.items.completeCatalog'),
          href: locales[locale].catalogPath,
        },
        { title: t('footerColumns.programs.items.afiUCircles'), href: locales[locale].circlesPath },
        {
          title: t('footerColumns.programs.items.programsAndMicroprograms'),
          href: locales[locale].programsPath,
        },
      ],
    },
  ]
  const laFamilleEdgendaColumns: FooterColumnData[] = [
    {
      title: t('edgenda'),
      rootUrl: t('edgendaRootUrl'),
      items: [
        { title: t('strategyOperations'), href: t('edgendaStrategyUrl') },
        { title: t('coachingLeadership'), href: t('edgendaCoachingUrl') },
        { title: t('emergingTechnologies'), href: t('edgendaTechnologyUrl') },
        { title: t('learning'), href: t('edgendaLearningUrl') },
      ],
    },
    {
      title: t('afiByEdgenda'),
      rootUrl: t('afiByEdgendaRootUrl'),
      items: [
        { title: t('designDevelopment'), href: t('afiByEdgendaLearningExperiencesUrl') },
        {
          title: t('rapidELearning'),
          href: t('afiByEdgendaFastNumericLearningsUrl'),
        },
        { title: t('customPrograms'), href: t('afiByEdgendaPersonalizedProgramsUrl') },
        { title: t('businessCoaching'), href: t('afiByEdgendaCoachingUrl') },
      ],
    },
    {
      title: t('apprentxByEdgenda'),
      rootUrl: t('apprentxRootUrl'),
      items: [{ title: 'B12', href: t('apprentxB12Url') }],
    },
    {
      title: t('afiU'),
      rootUrl: locales[locale].homePath,
      items: [
        { title: t('footerColumns.afiU.items.catalogue'), href: locales[locale].catalogPath },
        {
          title: t('footerColumns.afiU.items.priorities'),
          href: locales[locale].organizationsPath,
        },
        {
          title: t('footerColumns.afiU.items.forOrganizations'),
          href: locales[locale].organizationsPath,
        },
        { title: t('footerColumns.afiU.items.liveAfiU'), href: locales[locale].vivreAfiUPath },
      ],
    },
  ]

  // Update the grid columns layout to accommodate the new structure
  const getGridColumnTemplate = () => {
    if (breakpoints.md) {
      return 'repeat(4, 1fr)'
    }
    return 'repeat(1, 1fr)'
  }

  // Get the columns in the order they should appear in the grid
  const getOrderedColumns = () => {
    const topics = websiteColumns[0] // Topics
    const priorities = websiteColumns[1] // Business Priorities
    const certs = websiteColumns[2] // Certifications
    const certsCont = websiteColumns[3] // Certifications (continued)
    const forOrgs = websiteColumns[4] // For Organizations
    const about = websiteColumns[5] // About
    const resources = websiteColumns[6] // Resources
    const programs = websiteColumns[7] // Programs

    if (breakpoints.md) {
      return [topics, priorities, certs, certsCont, forOrgs, programs, about, resources]
    }

    const newCerts = { ...certs, items: [...certs.items, ...certsCont.items] }

    return [topics, priorities, newCerts, forOrgs, programs, about, resources]
  }

  return (
    <Container tag='footer' className='bg-white pt-12 font-avenir flex flex-col'>
      {breakpoints.md ? (
        <>
          <div className='flex flex-row flex-wrap items-end mb-8'>
            <LocaleSelector className='ml-auto text-black hover:text-black hover:underline' />
          </div>
        </>
      ) : (
        <Newsletter className='mb-8 ml-auto' style={{ maxWidth: '500px' }} />
      )}
      {breakpoints.md ? (
        <>
          <div className='flex flex-row items-start'>
            <div className='items-start mr-7' style={{ width: '220px' }}>
              <img src={afiUBlackLogo} />
            </div>

            <div className='flex flex-row gap-7 flex-wrap'>
              <div
                className='grid gap-2 lg:gap-7'
                style={{ gridTemplateColumns: getGridColumnTemplate() }}
              >
                {getOrderedColumns().map((column, index) => (
                  <FooterColumn key={`${column.title}-${index}`} column={column} />
                ))}
              </div>
            </div>
          </div>
          <hr className='mt-16 mb-12' />
        </>
      ) : (
        <div className='flex flex-col gap-2'>
          <img className='ml-auto' src={afiUBlackLogo} />
          {getOrderedColumns().map((column, index) => (
            <FooterMobileColumn key={`${column.title}-${index}`} column={column} />
          ))}
          <hr className='border-black mt-4 mb-6' />
        </div>
      )}
      <h2 className='text-r-2xl mb-2 block md3:hidden'>{t('edgendaFamily')}</h2>
      {breakpoints.md ? (
        <div className='flex flex-row items-start'>
          <div className='mr-7' style={{ width: '220px' }}>
            <h2 className='text-r-2xl' style={{ maxWidth: '12ch' }}>
              {t('edgendaFamily')}
            </h2>
          </div>

          <div className='flex flex-row gap-7 flex-wrap'>
            {laFamilleEdgendaColumns.map((column) => (
              <FooterColumn key={column.title} column={column} />
            ))}
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-2 items-start'>
          {laFamilleEdgendaColumns.map((column) => (
            <FooterMobileColumn key={column.title} column={column} />
          ))}
          <hr className='border-black mt-4 mb-6 w-full' />
          <LocaleSelector className='text-black hover:text-black hover:underline' />
        </div>
      )}
      <div className='mb-10 mt-10 md:mt-20 flex flex-row gap-x-12'>
        <div className='flex w-full md:w-3/5 flex-col'>
          <div className='flex flex-col md3:flex-row items-start justify-between gap-x-10 gap-y-4'>
            <div>
              <p className='text-r-lg mb-4'>
                {t('needAssistance')}{' '}
                <a className='text-black underline' href={t('takeMeetingUrl')}>
                  {t('talkSales')}
                </a>
                {` ${t('orCall')} `}
                <a className='text-black underline' href='tel:+18776242344'>
                  1 877 624.2344
                </a>
              </p>
            </div>
            <div className='order-first md3:order-last flex flex-row items-center gap-6 pr-5'>
              <a className='flex-shrink-0' href='https://www.linkedin.com/company/afi-u/'>
                <img src={linkedInLogo} alt='LinkedIn' />
              </a>
            </div>
          </div>
          <hr />
          <p className='text-r-sm mt-2'>
            <Link className='underline mt-2' to={locales[locale].confidentialityPath}>
              {t('privacyPolicy')}
            </Link>
            {` | ${t('privacyText')}`}
          </p>
          <p className='text-r-sm mt-2'>{t('edgendaCopyright')}</p>
          <a className='cky-banner-element cursor-pointer text-xs mt-2'>
            {t('revamp:cookieSettings')}
          </a>
        </div>
        {breakpoints.md && <Newsletter className='ml-auto' />}
      </div>
    </Container>
  )
}

function Newsletter({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation('revamp')

  return (
    <div className={cx('flex flex-col gap-y-2 items-end', className)} {...rest}>
      <p className='text-sm font-bold' style={{ textAlign: 'end', maxWidth: '35ch' }}>
        {t('newsletterText')}
      </p>
      <a
        href={t('newsletterLink')}
        className='text-r-base bg-black text-white rounded-sm hover:bg-revamp-neutral-600 font-bold hover:text-white px-4 py-1'
      >
        {t('newsletterSubscribe')}
      </a>
    </div>
  )
}
